import React, { useEffect, useState, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { Container, Typography, IconButton, Button, TextField } from "@mui/material";
import ArrowCircleLeftOutlinedIcon from "@mui/icons-material/ArrowCircleLeftOutlined";
import { formatDate } from "../../utils/utils.js";
import {
	fetchCustomerDatabyUploadDate,
	updateCustomerCheckboxes,
} from "../../services/dataModule/dataReview.js";
import AlertComponent from "../../common/components/AlertComponent.jsx";
import {
	SAVE_CUSTOMER_MESSAGES,
	UPLOAD_CUSTOMER_TABLE_COLUMNS,
	DATA_REVIEW_CONSTANTS,
} from "../../common/constants/dataModule/dataReviewConstants.js";
import { updateCustomerData } from "../../services/dataModule/dataReview.js";
import { SORT_ORDERS } from "../../common/constants/commonConstants.js";
import { handleSort, getComparator } from "../../hooks/useTable.js";
import {
	BUTTON_COLORS,
	SIZES,
	VARIANTS,
	ALIGN,
	TYPOGRAPHY_VARIANT,
	STATUS,
} from "../../common/constants/commonConstants.js";
import CustomerEditDailog from "./CustomerEditDailog.jsx";
import CustomerByUploadTable from "./CustomerByUploadTable.jsx";

const CustomerByUpload = ({ uploaddate, campaignId, onBack }) => {
	const [isLoading, setIsLoading] = useState(true);
	const [customerDetailsByDate, setCustomerDetailsByDate] = React.useState([]);
	const [alert, setAlert] = useState({ message: "", severity: "" });
	const [openModal, setOpenModal] = useState(false);
	const [selectedCustomer, setSelectedCustomer] = useState(null);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
	const [orderBy, setOrderBy] = useState("firstname");
	const [searchTerm, setSearchTerm] = React.useState("");

	useEffect(() => {
		const loadCustomerData = async (uploaddate, campaignId) => {
			try {
				setIsLoading(true);
				const fetchedData = await fetchCustomerDatabyUploadDate(uploaddate, campaignId);
				setCustomerDetailsByDate(fetchedData);
				setIsLoading(false);
			} catch (error) {
				console.error("Error fetching customer data:", error);
			} finally {
				setIsLoading(false);
			}
		};
		loadCustomerData(uploaddate, campaignId);
	}, [uploaddate, campaignId]);

	const customerList = customerDetailsByDate.map((customer) => ({
		...customer,
		name: `${customer.firstname ?? ""} ${customer.lastname ?? ""}`.trim(),
	}));

	useEffect(() => {
		setPage(0);
	}, [searchTerm]);

	const { customerDateToDisplay, filteredCustomerListLength } = useMemo(() => {
		const searchText = searchTerm.toLowerCase().trim();

		const filteredCustomers = customerList.filter((customer) => {
			const customerId = customer.customerid?.toString().toLowerCase() || "";
			const firstName = customer.firstname?.toLowerCase() || "";
			const lastName = customer.lastname?.toLowerCase() || "";
			const fullName = customer.name?.toLowerCase() || "";

			return (
				customerId.includes(searchText) ||
				firstName.includes(searchText) ||
				lastName.includes(searchText) ||
				fullName.includes(searchText)
			);
		});

		return {
			customerDateToDisplay: filteredCustomers
				.sort(getComparator(order, orderBy))
				.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
			filteredCustomerListLength: filteredCustomers.length,
		};
	}, [customerList, searchTerm, order, orderBy, page, rowsPerPage]);

	const handleEditCustomer = async (updatedCustomer) => {
		try {
			await updateCustomerData(updatedCustomer.id, updatedCustomer);
			setOpenModal(false);
			updateCustomerRecordWithList(updatedCustomer);
			showMessage({
				message: SAVE_CUSTOMER_MESSAGES.CUSTOMER_UPDATED,
				severity: STATUS.SUCCESS,
			});
			setSelectedCustomer(null);
		} catch (error) {
			showMessage({
				message: SAVE_CUSTOMER_MESSAGES.CUSTOMER_UPDATED_ERROR,
				severity: STATUS.ERROR,
			});
		}
	};

	const updateCustomerRecordWithList = async (updatedCustomer) => {
		if (updatedCustomer) {
			setCustomerDetailsByDate((prevList) =>
				prevList.map((cust) =>
					cust.id === updatedCustomer.id ? updatedCustomer : cust
				)
			);
		}
	};

	const handlePublishConversUpdate = (updatedList) => {
		setCustomerDetailsByDate((prevList) =>
			prevList.map((cust) => {
				const updated = updatedList.find((u) => u.id === cust.id);
				return updated ? { ...cust, ...updated } : cust;
			})
		);
	};

	const handelSaveChanges = async () => {
		try {
			const bulkUpdateData = customerDetailsByDate.map((customer) => ({
				id: customer.id,
				published: customer.published,
				convapproved: customer.convapproved,
			}));
			await updateCustomerCheckboxes(bulkUpdateData);
			showMessage({
				message: SAVE_CUSTOMER_MESSAGES.BULK_UPDATED,
				severity: STATUS.SUCCESS,
			});
		} catch (error) {
			showMessage({
				message: SAVE_CUSTOMER_MESSAGES.BULK_UPDATED_ERROR,
				severity: STATUS.ERROR,
			});
		}
	};

	const showMessage = (msg) => {
		setAlert(msg);
		setTimeout(() => {
			setAlert("");
		}, 3000);
	};

	const handleEditCustomerClick = (customer) => {
		setSelectedCustomer(customer);
		setOpenModal(true);
	};

	const handleRequestSort = (property) => {
		handleSort(order, orderBy, property, setOrder, setOrderBy);
	};

	return (
		<Container maxWidth={false} disableGutters>
			{alert.message && (
				<AlertComponent message={alert.message} severity={alert.severity} />
			)}
			<Grid container spacing={2} sx={{ mb: 2, alignItems: ALIGN.CENTER }}>
				<Grid size={SIZES.AUTO}>
					<IconButton
						variant={VARIANTS.OUTLINED}
						color={BUTTON_COLORS.SECONDARY}
						onClick={onBack}
					>
						<ArrowCircleLeftOutlinedIcon />
					</IconButton>
				</Grid>
				<Grid size={SIZES.GROW}>
					<Typography variant={TYPOGRAPHY_VARIANT.H6}>
						{formatDate(uploaddate)}
					</Typography>
				</Grid>
			</Grid>
			<Grid container spacing={2} sx={{ mb: 2, alignItems: ALIGN.CENTER }}>
				<Grid size={SIZES.GROW}>
					<TextField
						fullWidth
						label={DATA_REVIEW_CONSTANTS.CUSTOMER_SEARCH_PLACEHOLDER}
						variant={VARIANTS.OUTLINED}
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						size={SIZES.SMALL}
					/>
				</Grid>
				<Grid size={SIZES.AUTO}>
					<Button
						variant={VARIANTS.CONTAINED}
						color={BUTTON_COLORS.PRIMARY}
						onClick={() => handelSaveChanges(uploaddate)}
					>
						Save Changes
					</Button>
				</Grid>
			</Grid>
			<CustomerByUploadTable
				page={page}
				order={order}
				setPage={setPage}
				orderBy={orderBy}
				rowsPerPage={rowsPerPage}
				isLoading={isLoading}
				customerToDisplay={customerDateToDisplay}
				setRowsPerPage={setRowsPerPage}
				columns={UPLOAD_CUSTOMER_TABLE_COLUMNS}
				onEdit={handleEditCustomerClick}
				handleRequestSort={handleRequestSort}
				filteredCustomerListLength={filteredCustomerListLength}
				onPublishConversUpdate={handlePublishConversUpdate}
			/>
			<CustomerEditDailog
				customer={selectedCustomer}
				uploaddate={uploaddate}
				openModal={openModal}
				onSave={handleEditCustomer}
				onClose={() => setOpenModal(false)}
			/>
		</Container>
	);
};

export default CustomerByUpload;